(() => {
  'use strict';

  const STORAGE_KEY = 'pk:recently-viewed:v1';
  const MAX_HISTORY = 20;
  const HANDLE_RE = /^[a-z0-9](?:[a-z0-9-]{0,253}[a-z0-9])?$/;

  const isValidHandle = (value) => typeof value === 'string' && HANDLE_RE.test(value);
  const cleanText = (value, limit) => typeof value === 'string' ? value.trim().slice(0, limit) : '';
  const normalizeProductUrl = (value, handle, origin) => {
    if (!isValidHandle(handle) || typeof value !== 'string') return null;
    try {
      const url = new URL(value, origin);
      if (url.origin !== origin || url.pathname.replace(/\/$/, '') !== `/products/${handle}`) return null;
      return `/products/${handle}`;
    } catch (_) {
      return null;
    }
  };
  const normalizeImageUrl = (value, origin) => {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, origin);
      if (!['http:', 'https:'].includes(url.protocol)) return null;
      return url.href;
    } catch (_) {
      return null;
    }
  };
  const normalizeRecord = (record, origin) => {
    if (!record || typeof record !== 'object' || !isValidHandle(record.handle)) return null;
    const url = normalizeProductUrl(record.url, record.handle, origin);
    const image = normalizeImageUrl(record.image, origin);
    const title = cleanText(record.title, 240);
    const priceText = cleanText(record.priceText, 80);
    const viewedAt = Number(record.viewedAt);
    if (!url || !image || !title || !priceText || !Number.isFinite(viewedAt) || viewedAt < 0) return null;
    return { version: 1, handle: record.handle, title, url, image, priceText, viewedAt };
  };
  const mergeHistory = (items, current, origin) => {
    const normalizedCurrent = normalizeRecord(current, origin);
    const normalizedItems = Array.isArray(items) ? items.map((item) => normalizeRecord(item, origin)).filter(Boolean) : [];
    const combined = normalizedCurrent ? [normalizedCurrent, ...normalizedItems] : normalizedItems;
    const seen = new Set();
    return combined
      .sort((a, b) => b.viewedAt - a.viewedAt)
      .filter((item) => !seen.has(item.handle) && seen.add(item.handle))
      .slice(0, MAX_HISTORY);
  };
  const selectCandidates = (items, cartHandles, limit) => {
    const excluded = cartHandles instanceof Set ? cartHandles : new Set();
    const safeLimit = Math.max(0, Math.min(MAX_HISTORY, Number(limit) || 0));
    return items.filter((item) => !excluded.has(item.handle)).slice(0, safeLimit);
  };
  const readHistory = (storage, origin) => {
    try {
      const raw = storage.getItem(STORAGE_KEY);
      if (raw === null) return { ok: true, items: [] };
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return { ok: false, items: [] };
      return { ok: true, items: mergeHistory(parsed, null, origin) };
    } catch (_) {
      return { ok: false, items: [] };
    }
  };
  const writeHistory = (storage, items) => {
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(items));
      return true;
    } catch (_) {
      return false;
    }
  };
  const isUsableProduct = (product, expectedHandle) => Boolean(
    product && product.available === true && product.handle === expectedHandle && product.featured_image
  );

  const api = Object.freeze({
    STORAGE_KEY,
    MAX_HISTORY,
    isValidHandle,
    normalizeRecord,
    mergeHistory,
    selectCandidates,
    readHistory,
    writeHistory,
    isUsableProduct,
  });
  globalThis.PKRecentlyViewed = api;

  if (typeof document === 'undefined') return;

  let storage;
  try {
    storage = globalThis.localStorage;
  } catch (_) {
    return;
  }
  if (!storage) return;

  const origin = location.origin;
  const productCache = new Map();
  const renderVersions = new WeakMap();
  let latestCartHandles = null;
  const getRoot = () => globalThis.Shopify?.routes?.root || '/';
  const getProduct = (handle) => {
    if (!productCache.has(handle)) {
      const root = getRoot().endsWith('/') ? getRoot() : `${getRoot()}/`;
      const request = fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error('product lookup failed')))
        .then((product) => isUsableProduct(product, handle) ? product : null)
        .catch(() => null);
      productCache.set(handle, request);
    }
    return productCache.get(handle);
  };
  const parseCartHandles = (container) => {
    try {
      const node = container.querySelector('[data-pk-recently-viewed-cart-handles]');
      const values = JSON.parse(node?.textContent || '[]');
      if (!Array.isArray(values)) return null;
      return new Set(values.filter(isValidHandle));
    } catch (_) {
      return null;
    }
  };
  const getLimit = (container) => {
    const mobile = globalThis.matchMedia?.('(max-width: 749px)').matches;
    return Number(mobile ? container.dataset.mobileLimit : container.dataset.desktopLimit) || 0;
  };
  const formatPrice = (product, record, moneyFormat) => {
    if (typeof globalThis.Shopify?.formatMoney === 'function' && Number.isFinite(Number(product.price))) {
      try { return globalThis.Shopify.formatMoney(product.price, moneyFormat); } catch (_) {}
    }
    if (Number.isFinite(Number(product.price))) {
      try { return new Intl.NumberFormat(document.documentElement.lang || "en", { style: "currency", currency: globalThis.Shopify?.currency?.active || "USD" }).format(Number(product.price) / 100); } catch (_) {}
    }
    return record.priceText;
  };
  const buildCard = (record, product, moneyFormat) => {
    const item = document.createElement('article');
    item.className = 'pk-recently-viewed__item';
    item.setAttribute('role', 'listitem');

    const link = document.createElement('a');
    link.className = 'pk-recently-viewed__link';
    link.href = `/products/${record.handle}`;
    link.setAttribute('aria-label', `View ${cleanText(product.title, 240) || record.title}`);

    const imageWrap = document.createElement('span');
    imageWrap.className = 'pk-recently-viewed__image-wrap';
    const image = document.createElement('img');
    image.className = 'pk-recently-viewed__image';
    image.src = normalizeImageUrl(product.featured_image, origin) || record.image;
    image.alt = cleanText(product.title, 240) || record.title;
    image.loading = 'lazy';
    image.width = 320;
    image.height = 320;
    imageWrap.append(image);
    if (Number(product.price) > 100000) {
      const seal = document.createElement('span');
      seal.className = 'pk-collector-seal pk-collector-seal--compact notranslate';
      seal.setAttribute('aria-label', "Collector's piece");
      seal.setAttribute('translate', 'no');
      const top = document.createElement('span');
      top.className = 'pk-collector-seal__word pk-collector-seal__word--top';
      top.setAttribute('aria-hidden', 'true');
      top.textContent = 'COLLECTOR';
      const monogram = document.createElement('span');
      monogram.className = 'pk-collector-seal__monogram';
      monogram.setAttribute('aria-hidden', 'true');
      monogram.textContent = 'PK';
      const bottom = document.createElement('span');
      bottom.className = 'pk-collector-seal__word pk-collector-seal__word--bottom';
      bottom.setAttribute('aria-hidden', 'true');
      bottom.textContent = 'PIECE';
      seal.append(top, monogram, bottom);
      imageWrap.append(seal);
    }

    const copy = document.createElement('span');
    copy.className = 'pk-recently-viewed__copy';
    const title = document.createElement('span');
    title.className = 'pk-recently-viewed__product-title';
    title.textContent = cleanText(product.title, 240) || record.title;
    const price = document.createElement('span');
    price.className = 'pk-recently-viewed__price';
    if (globalThis.PKOpeningSale && Number.isFinite(Number(product.price))) {
      globalThis.PKOpeningSale.renderPrice(price, product, Number(product.price), amount => formatPrice({ ...product, price: amount }, record, moneyFormat));
    } else price.textContent = formatPrice(product, record, moneyFormat);
    copy.append(title, price);
    link.append(imageWrap, copy);
    item.append(link);
    return item;
  };
  const renderContainer = async (container, overrideCartHandles) => {
    const renderVersion = (renderVersions.get(container) || 0) + 1;
    renderVersions.set(container, renderVersion);
    container.hidden = true;
    const rail = container.querySelector('[data-pk-recently-viewed-rail]');
    if (!rail) return;
    rail.replaceChildren();
    const history = readHistory(storage, origin);
    if (!history.ok || history.items.length === 0) return;
    const cartHandles = overrideCartHandles || parseCartHandles(container);
    if (!(cartHandles instanceof Set)) return;
    const candidates = selectCandidates(history.items, cartHandles, MAX_HISTORY);
    const products = await Promise.all(candidates.map((record) => getProduct(record.handle)));
    if (renderVersions.get(container) !== renderVersion || !container.isConnected) return;
    const complete = candidates
      .map((record, index) => ({ record, product: products[index] }))
      .filter(({ product }) => product)
      .slice(0, getLimit(container));
    if (complete.length === 0) return;
    const fragment = document.createDocumentFragment();
    complete.forEach(({ record, product }) => fragment.append(buildCard(record, product, container.dataset.moneyFormat || '')));
    rail.append(fragment);
    container.hidden = false;
  };
  const isDrawerReady = (container) => {
    if (container.dataset.pkRecentlyViewedContext !== 'cart-drawer') return true;
    return container.closest('cart-drawer')?.classList.contains('active') === true;
  };
  const renderAll = (cartHandles, includeClosedDrawer = false) => {
    const effectiveCartHandles = cartHandles instanceof Set ? cartHandles : latestCartHandles;
    document.querySelectorAll('[data-pk-recently-viewed]').forEach((container) => {
      if (!includeClosedDrawer && !isDrawerReady(container)) return;
      renderContainer(container, effectiveCartHandles).catch(() => { container.hidden = true; });
    });
  };

  const source = document.querySelector('[data-pk-recently-viewed-source]');
  if (source) {
    const current = normalizeRecord({
      version: 1,
      handle: source.dataset.productHandle,
      title: source.dataset.productTitle,
      url: source.dataset.productUrl,
      image: source.dataset.productImage,
      priceText: source.dataset.productPrice,
      viewedAt: Date.now(),
    }, origin);
    const history = readHistory(storage, origin);
    if (current && history.ok) writeHistory(storage, mergeHistory(history.items, current, origin));
  }

  renderAll();

  document.addEventListener('click', (event) => {
    if (!event.target.closest?.('#cart-icon-bubble, #pk-chosen-pieces-cart-trigger')) return;
    setTimeout(() => renderAll(latestCartHandles), 0);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const items = event?.cartData?.items;
      const handles = Array.isArray(items)
        ? new Set(items.map((item) => item.handle || item.product_handle).filter(isValidHandle))
        : null;
      if (handles) latestCartHandles = handles;
      renderAll(latestCartHandles);
    });
  }

  const observer = new MutationObserver((mutations) => {
    const hasNewContainer = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) =>
      node.nodeType === 1 && (node.matches?.('[data-pk-recently-viewed]') || node.querySelector?.('[data-pk-recently-viewed]'))
    ));
    const drawerOpened = mutations.some((mutation) =>
      mutation.type === 'attributes' && mutation.target.matches?.('cart-drawer.active')
    );
    if (hasNewContainer || drawerOpened) renderAll(latestCartHandles);
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
})();

